import { createStore } from 'vuex'
import auth from './modules/auth'
import user from './modules/user'
import settings from './modules/settings'

// Create a unique ID for notifications
const generateId = () => {
  return '_' + Math.random().toString(36).substr(2, 9);
};

// Create a new store instance
export default createStore({
  state: {
    // Global application state
    suppliers: [],
    loading: false,
    notification: null, // Single active notification for toast
    notifications: [], // Array for potential future notification list
    currentLanguage: localStorage.getItem('language') || 'en',
    // Entity counts for sidebar/overview display
    entityCounts: {
      certificatesCount: 0,
      materialCategoriesCount: 0,
      portsCount: 0
    },
    // Incremented whenever the tenant subscription changes (activate/cancel/resume)
    // so widgets showing subscription info (e.g. sidebar) can reload it
    subscriptionRefreshKey: 0
  },
  
  mutations: {
    // Mutations are synchronous transactions
    SET_SUPPLIERS(state, suppliers) {
      state.suppliers = suppliers
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_LANGUAGE(state, language) {
      state.currentLanguage = language;
      localStorage.setItem('language', language);
    },
    SET_NOTIFICATION(state, notification) {
      state.notification = notification ? {
        id: generateId(),
        message: notification.message,
        type: notification.type || 'info',
        timestamp: new Date()
      } : null;
    },
    ADD_NOTIFICATION(state, notification) {
      state.notifications.push({
        id: generateId(),
        message: notification.message,
        type: notification.type || 'info',
        timestamp: new Date()
      });
    },
    REMOVE_NOTIFICATION(state, id) {
      state.notifications = state.notifications.filter(n => n.id !== id);
    },
    CLEAR_NOTIFICATIONS(state) {
      state.notifications = [];
    },
    // Signal that the tenant subscription changed
    bumpSubscriptionRefresh(state) {
      state.subscriptionRefreshKey++;
    },
    // Update entity counts
    updateEntityCounts(state, counts) {
      state.entityCounts = {
        ...state.entityCounts,
        ...counts
      };
    }
  },
  
  actions: {
    // Actions can contain async operations
    fetchSuppliers({ commit }) {
      commit('SET_LOADING', true)
      // Simulate API call
      return new Promise((resolve) => {
        setTimeout(() => {
          const suppliers = [
            { id: 1, name: 'Acme Supplies', status: 'active' },
            { id: 2, name: 'Global Materials', status: 'pending' },
            { id: 3, name: 'East Coast Distribution', status: 'active' }
          ]
          commit('SET_SUPPLIERS', suppliers)
          commit('SET_LOADING', false)
          resolve(suppliers)
        }, 1000)
      })
    },
    
    // Global notification system
    notify({ commit }, notification) {
      // If notification is a string, convert to object with default type
      if (typeof notification === 'string') {
        notification = { 
          message: notification,
          type: 'info'
        };
      }
      
      // Set current active notification
      commit('SET_NOTIFICATION', notification);
      
      // Also add to notifications array for history
      commit('ADD_NOTIFICATION', notification);
      
      // Auto clear notification after timeout
      setTimeout(() => {
        commit('SET_NOTIFICATION', null);
      }, notification.timeout || 5000);
    },
    clearNotification({ commit }) {
      commit('SET_NOTIFICATION', null);
    },
    removeNotification({ commit }, id) {
      commit('REMOVE_NOTIFICATION', id);
    },
    clearNotifications({ commit }) {
      commit('CLEAR_NOTIFICATIONS');
    },
    setLanguage({ commit }, language) {
      commit('SET_LANGUAGE', language);
    },
    setLoading({ commit }, status) {
      commit('SET_LOADING', status);
    }
  },
  
  getters: {
    // Computed state properties
    activeSuppliers: (state) => state.suppliers.filter(s => s.status === 'active'),
    pendingSuppliers: (state) => state.suppliers.filter(s => s.status === 'pending'),
    isLoading: state => state.loading,
    // Current notification for toast
    getCurrentNotification: state => state.notification,
    // Return notifications sorted by creation time (newest first)
    getNotifications: state => [...state.notifications].sort((a, b) => b.timestamp - a.timestamp),
    getCurrentLanguage: state => state.currentLanguage,
    // Get entity counts
    getEntityCounts: state => state.entityCounts
  },
  
  modules: {
    // Register modules
    auth,
    user,
    settings
  }
}) 